import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Button, Dialog } from '@mui/material';
import { SystemTable } from '@sharedCR/common/SystemTable';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { useLoanEditorRunnerContext } from 'app/pages/loanApplication/LoanHeaders/LoanInfoHeader/LoanActionsControls';
import { FormViewer } from 'app/pages/system/forms';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import TaskActionRunner from 'app/shared/modules/TaskActionRunner';
import TaskInfo from 'app/shared/modules/TaskInfo';
import SlaGroupInfoTable from 'app/shared/modules/TaskSlaGroupInfoTable';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import withPagePermissions, { WithPagePermissionsPropTypes } from 'hocs/withPagePermissions';
import withPreparedData from 'hocs/withPreparedData';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { FormViewerNavigationType } from 'constants/forms';
import { TASK_AUDIENCE_COLUMN_KEY } from 'constants/loanSummaryDrilldown';
import { PAGE_CODES } from 'constants/pageCodes';
import { TaskStatus } from 'constants/tasks';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import useSort from 'hooks/useSort';
import { jsonStringify } from 'utils/json';

import { getLoanSpecificForms, setFormNavigationTypeAction } from 'store/forms/actions';
import { getRolesAction, openDialogAction, showMessageAction } from 'store/global/actions';
import {
  getApplicationBorrowersAction,
  getApplicationIncomesAction,
  getLoanAssetsAction,
  getLoanApplicationTasksAction,
  getTaskSlaGroupsByApplicationIdAction,
  showMessageThatCurrentUserCantChangeLoanAction,
} from 'store/loanApplications/actions';
import {
  selectGroupedFilteredTasks,
  selectIsCurrentUserCanChangeLoan,
  selectIsLoanCanceled,
  selectIsLoanInitiatedWithErrors,
} from 'store/loanApplications/selectors';
import {
  getLoanRolesAction,
  getTaskCategoriesAction,
  getTaskTypeDynamicFieldAction,
  getTaskTypesAction,
} from 'store/system/actions';
import { getSlaGroupsAction } from 'store/underwriting/actions';
import { getUsersAction } from 'store/users/actions';

import { uncompletedLoanErrorMessageParams } from '../../LoanApplication.constants';

import useFormViewerClose from './hooks/useFormViewerClose';
import useTaskAudienceDrilldown from './hooks/useTaskAudienceDrilldown';
import TaskAssignmentForm from './TaskAssignmentForm';
import TaskHistoryTable from './TaskHistoryTable';
import { getTasksTableConfig } from './Tasks.config';
import { taskTableDefaultOrder } from './Tasks.constants';
import { TasksTestIds } from './Tasks.testIds';
import { getSelectOptions, prepareTaskRow } from './Tasks.utils';

const Tasks = ({
  status,
  groupedTasks,
  isManageAllowed,
  setIsLoading,
  columnFilters,
  applyFilters,
  onColumnFiltersChange,
}) => {
  const dispatch = useDispatch();

  const loanId = useLoanId();

  const { applicationId, ...paramsFromUrl } = useParams();
  const params = { ...paramsFromUrl, loanId, applicationId };
  const paramsToDeps = jsonStringify(params);

  const { order, onRequestSort } = useSort(taskTableDefaultOrder);

  const isLoanInitedWithErrors = useSelector(selectIsLoanInitiatedWithErrors);
  const isCurrentUserCanChangeLoan = useSelector(selectIsCurrentUserCanChangeLoan);
  const isLoanCanceled = useSelector(selectIsLoanCanceled);

  const taskActionRunnerContext = useLoanEditorRunnerContext();

  const onTaskActionRefresh = useCallback(
    () => dispatch(getLoanApplicationTasksAction()),
    [dispatch],
  );

  const [formParams, setFormParams] = useState(null);

  const isOngoingTab = status === TaskStatus.NEW;
  const shouldShowButton = !isLoanCanceled && isManageAllowed && isOngoingTab;

  const handleFormClose = useFormViewerClose({ formParams, setFormParams, setIsLoading });

  const preparedTasks = useMemo(
    () => (groupedTasks[status] || []).map(prepareTaskRow),
    [groupedTasks, status],
  );

  const allLoanTaskRows = useMemo(
    () => Object.values(groupedTasks).flat().map(prepareTaskRow),
    [groupedTasks],
  );

  const loanTaskTypeLabels = useMemo(
    () => _.uniq(allLoanTaskRows.map(({ taskTypeLabel }) => taskTypeLabel).filter(Boolean)),
    [allLoanTaskRows],
  );

  useTaskAudienceDrilldown({ onColumnFiltersChange, availableLabels: loanTaskTypeLabels });

  const onOpenTaskInfo = useCallback(
    taskId => {
      const task = preparedTasks.find(({ taskId: id }) => id === taskId);

      if (isLoanCanceled || !task) {
        return;
      }

      dispatch(
        openDialogAction({
          header: 'Task Info',
          body: dialogProps => (
            <TaskActionRunner
              runnerContext={taskActionRunnerContext}
              task={task}
              onTaskRefresh={onTaskActionRefresh}
            >
              <TaskInfo
                selectedTabStatus={status}
                task={task}
                {...dialogProps}
                formName={FormNames.TASK_INFORMATION}
              />
            </TaskActionRunner>
          ),
        }),
      );
    },
    [dispatch, isLoanCanceled, preparedTasks, status, taskActionRunnerContext, onTaskActionRefresh],
  );

  const onOpenTaskHistory = useCallback(
    (event, { taskId }) => {
      event.stopPropagation();

      dispatch(
        openDialogAction({
          header: 'Task Info',
          body: dialogProps => <TaskHistoryTable taskId={taskId} {...dialogProps} />,
          size: ModalSize.XL,
        }),
      );
    },
    [dispatch],
  );

  const onOpenSlaGroupInfo = useCallback(
    (event, { taskId, slaGroupId, name }) => {
      event.stopPropagation();

      dispatch(
        openDialogAction({
          size: ModalSize.LARGE,
          header: `${name} SLA Group Info`,
          body: dialogProps => (
            <SlaGroupInfoTable slaGroupId={slaGroupId} taskId={taskId} {...dialogProps} />
          ),
        }),
      );
    },
    [dispatch],
  );

  const tasksTableConfig = useMemo(
    () =>
      getTasksTableConfig({
        status,
        taskTypeOptions: getSelectOptions(
          allLoanTaskRows,
          'taskTypeLabel',
          columnFilters?.[TASK_AUDIENCE_COLUMN_KEY]?.values,
        ),
        onOpenTaskHistory,
        onOpenSlaGroupInfo,
      }),
    [status, allLoanTaskRows, columnFilters, onOpenTaskHistory, onOpenSlaGroupInfo],
  );

  const filteredTasks = useMemo(
    () => applyFilters(preparedTasks, tasksTableConfig),
    [applyFilters, preparedTasks, tasksTableConfig],
  );

  const orderedTasks = useMemo(
    () =>
      _.orderBy(
        filteredTasks,
        [
          task => {
            const orderField = _.get(task, order.id);

            return _.isString(orderField) ? orderField?.toLowerCase() : orderField;
          },
        ],
        [order.direction],
      ),
    [order, filteredTasks],
  );

  const onAssignTask = useCallback(() => {
    if (isLoanInitedWithErrors) {
      return dispatch(showMessageAction(uncompletedLoanErrorMessageParams));
    }

    if (!isCurrentUserCanChangeLoan) {
      return dispatch(showMessageThatCurrentUserCantChangeLoanAction());
    }

    dispatch(
      openDialogAction({
        header: 'Assign Task',
        body: dialogProps => (
          <TaskAssignmentForm onFinishUploadForm={setFormParams} {...dialogProps} />
        ),
      }),
    );
  }, [isLoanInitedWithErrors, isCurrentUserCanChangeLoan, dispatch]);

  const onAssignFormSuccessCallback = useCallback(() => {
    setFormParams(null);
  }, []);

  useEffectSafeStrictMode(() => {
    setIsLoading(true);
    Promise.all([
      loanId && dispatch(getLoanApplicationTasksAction({ applicationId })),
      dispatch(getTaskCategoriesAction()),
      dispatch(getTaskTypesAction()),
      dispatch(getTaskTypeDynamicFieldAction()),
      dispatch(getLoanSpecificForms(params)),
      dispatch(getApplicationBorrowersAction(params)),
      dispatch(getApplicationIncomesAction(params)),
      dispatch(getLoanAssetsAction(params)),
      dispatch(getTaskSlaGroupsByApplicationIdAction({ applicationId })),
      dispatch(getSlaGroupsAction()),
      dispatch(getUsersAction({ includeDeleted: true })),
      dispatch(getRolesAction({ includeDeletedRoles: true })),
      dispatch(getLoanRolesAction({ includeDeletedRoles: true })),
    ])
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading, paramsToDeps]);

  useEffect(() => {
    dispatch(setFormNavigationTypeAction(FormViewerNavigationType.STATE));
  }, [dispatch]);

  return (
    <>
      <Dialog
        disableEnforceFocus
        aria-labelledby="dialog-title"
        data-testid={TasksTestIds.FORM_VIEWER_DIALOG}
        maxWidth="lg"
        open={!!formParams}
      >
        {!!formParams && (
          <FormViewer
            isDisableResetStateAction
            isNeedCloseButton
            isShowAssignFormIconButton
            formParams={formParams}
            onAssignFormSuccessCallback={onAssignFormSuccessCallback}
            onClose={handleFormClose}
          />
        )}
      </Dialog>

      <SystemTable
        columnFilters={columnFilters}
        config={tasksTableConfig}
        data={orderedTasks}
        order={order}
        skeletonRows={3}
        testId={TasksTestIds.TABLE}
        onColumnFiltersChange={onColumnFiltersChange}
        onRequestSort={onRequestSort}
        onRowClick={onOpenTaskInfo}
      />

      {shouldShowButton && (
        <Button
          aria-label="Assign task"
          className="w-fit !mx-2 !mt-3"
          data-testid={TasksTestIds.ASSIGN_TASK_BUTTON}
          type="button"
          variant="contained"
          onClick={onAssignTask}
        >
          Add new task
        </Button>
      )}
    </>
  );
};

Tasks.propTypes = {
  applyFilters: PropTypes.func.isRequired,
  groupedTasks: PropTypes.object.isRequired,
  status: PropTypes.string.isRequired,
  onColumnFiltersChange: PropTypes.func.isRequired,
  columnFilters: PropTypes.object,
  ...WithPagePermissionsPropTypes,
  ...WithLoadingPropTypes,
};

export default compose(
  withLoading,
  withPagePermissions(PAGE_CODES.TASKS),
  withPreparedData({
    dataSelectors: { groupedTasks: selectGroupedFilteredTasks },
  }),
  memo,
)(Tasks);
